import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import ArtistMembersTab from './ArtistMembersTab';
import { toggleAttended, fetchBatchEventStatus, getIdToken } from '../attendedUtils';
import { UploadModal } from './MyImages';

const INFO_TABS = ['Show Stats', 'Bio', 'Links', 'Photos', 'Additional Info'];
const PAGE_SIZE = 25;

// ── Parent tabs for Section 3 ─────────────────────────────────────────────────
const PARENT_TABS = ['Show Archive', 'Shows by Year', 'Members', 'Relationships', 'Locations'];
const SHOW_ARCHIVE_SUBTABS = ['Show History', 'Flyer History'];
const RELATIONSHIPS_SUBTABS = ['Artist Relationships', 'Festivals Played', 'Promoter Relationships', 'Venues Played'];
const LOCATIONS_SUBTABS = ['Geographic Breakdown', 'Shows Map'];

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ currentPage, totalItems, onPageChange }) {
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return (
        <div style={styles.pagination}>
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}>‹ Prev</button>
            {pages.map(p => (
                <button key={p} onClick={() => onPageChange(p)} style={{ ...styles.pageBtn, ...(p === currentPage ? styles.pageBtnActive : {}) }}>{p}</button>
            ))}
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ ...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {}) }}>Next ›</button>
        </div>
    );
}

// ── Sub-tab bar ───────────────────────────────────────────────────────────────
function SubTabBar({ tabs, active, onChange }) {
    return (
        <div style={styles.subTabBar}>
            {tabs.map((tab, i) => (
                <button
                    key={tab}
                    onClick={() => onChange(i)}
                    style={{ ...styles.subTabBtn, ...(active === i ? styles.subTabBtnActive : {}) }}
                >
                    {tab}
                </button>
            ))}
        </div>
    );
}

// ── Photos Tab ────────────────────────────────────────────────────────────────
function PhotosTab({ artistId, artistName, defaultImageId, onDefaultChanged }) {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [settingDefault, setSettingDefault] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [showUpload, setShowUpload] = useState(false);

    const loadPhotos = useCallback(async () => {
        if (!artistId) return;
        setLoading(true);
        try {
            const token = await getIdToken();
            const res = await axios.get(`${config.API_URL}/images`, {
                params: { entityType: 'artist', entityId: artistId },
                headers: { Authorization: token },
            });
            const imgs = res.data.images || [];
            setImages(imgs);
            setError('');
            if (defaultImageId) {
                const defaultImg = imgs.find(i => i.id === defaultImageId);
                if (defaultImg) onDefaultChanged(defaultImg.id, defaultImg.image_url);
            }
        } catch {
            setError('Failed to load photos.');
        } finally {
            setLoading(false);
        }
    }, [artistId, defaultImageId, onDefaultChanged]);

    useEffect(() => { loadPhotos(); }, [loadPhotos]);

    const handleUploaded = (img) => {
        setImages(prev => [img, ...prev]);
        setShowUpload(false);
    };

    const [removingDefault, setRemovingDefault] = useState(false);

    const handleRemoveDefault = async () => {
        setRemovingDefault(true);
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: null, entityType: 'artist', entityId: artistId },
                { headers: { Authorization: token } }
            );
            onDefaultChanged(null, null);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to remove default image.');
        } finally {
            setRemovingDefault(false);
        }
    };

    const handleSetDefault = async (image) => {
        setSettingDefault(image.id);
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: image.id, entityType: 'artist', entityId: artistId },
                { headers: { Authorization: token } }
            );
            onDefaultChanged(image.id, image.image_url);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to set default image.');
        } finally {
            setSettingDefault(null);
        }
    };

    if (loading) return <div style={styles.emptyTab}>Loading photos…</div>;
    if (error) return <div style={styles.emptyTab}>{error}</div>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                    {defaultImageId && (
                        <button
                            onClick={handleRemoveDefault}
                            disabled={removingDefault}
                            style={{ background: 'transparent', border: '1px solid #555', borderRadius: '6px', color: '#aaa', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', opacity: removingDefault ? 0.6 : 1 }}
                        >
                            {removingDefault ? 'Removing…' : '✕ Remove Default'}
                        </button>
                    )}
                </div>
                <button onClick={() => setShowUpload(true)} style={styles.addPhotoBtn}>
                    + Add Photo
                </button>
            </div>

            {images.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>📷</div>
                    <div style={{ color: '#666', fontSize: '14px' }}>No photos yet for this artist.</div>
                </div>
            ) : (
                <div style={styles.photoGrid}>
                    {images.map(img => {
                        const isDefault = img.id === defaultImageId;
                        return (
                            <div key={img.id} style={{ ...styles.photoCard, ...(isDefault ? styles.photoCardDefault : {}) }}>
                                <div style={styles.photoThumb} onClick={() => setLightbox(img)}>
                                    <img src={img.image_url} alt={img.caption || artistName} style={styles.photoThumbImg} loading="lazy" />
                                    {isDefault && <span style={styles.defaultBadge}>★ Default</span>}
                                    {!img.is_public && <span style={styles.privateBadge}>🔒</span>}
                                </div>
                                <div style={styles.photoCardBody}>
                                    {img.caption && <div style={styles.photoCaption}>{img.caption}</div>}
                                    {!isDefault && img.is_public && (
                                        <button onClick={() => handleSetDefault(img)} disabled={settingDefault === img.id} style={styles.setDefaultBtn}>
                                            {settingDefault === img.id ? 'Setting…' : '★ Set as Default'}
                                        </button>
                                    )}
                                    {isDefault && <div style={styles.isDefaultLabel}>Current default photo</div>}
                                    {!img.is_public && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>Private — only visible to you</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {lightbox && (
                <div style={styles.lightboxOverlay} onClick={() => setLightbox(null)}>
                    <div style={{ position: 'relative' }}>
                        <img src={lightbox.image_url} alt={lightbox.caption || ''} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' }} />
                        {lightbox.caption && <div style={styles.lightboxCaption}>{lightbox.caption}</div>}
                        <button onClick={() => setLightbox(null)} style={styles.lightboxClose}>✕</button>
                    </div>
                </div>
            )}

            {showUpload && (
                <UploadModal
                    onClose={() => setShowUpload(false)}
                    onUploaded={handleUploaded}
                    presetEntityType="artist"
                    presetEntityId={artistId}
                    presetEntityName={artistName}
                />
            )}
        </div>
    );
}

// ── Show History Tab ──────────────────────────────────────────────────────────
function ShowHistoryTab({ shows, attendedMap, performedMap, workedMap, onToggleAttended, togglingMap }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded yet.</div>;
    const q = search.toLowerCase();
    const filtered = search ? shows.filter(show => {
        const dateStr = show.event_date ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';
        return [dateStr, show.city, show.state, show.country, show.venue_name, show.festival_name, show.other_artists].some(val => val && val.toLowerCase().includes(q));
    }) : shows;
    const totalShows = shows.length;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by date, city, state, country, venue, artists..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No shows match your search.</div> : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Show #</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>Additional Artists</th>
                                    <th style={styles.th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    const globalIdx = shows.indexOf(show);
                                    const showNum = totalShows - globalIdx;
                                    const dateObj = show.event_date ? new Date(show.event_date) : null;
                                    const formattedDate = dateObj ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—';
                                    const isAttended = attendedMap[show.event_id] === true;
                                    const isPerformed = performedMap[show.event_id] === true;
                                    const isWorked = workedMap[show.event_id] === true;
                                    const isToggling = togglingMap[show.event_id];
                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                <Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{showNum}</Link>
                                            </td>
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formattedDate}</td>
                                            <td style={styles.td}>{show.city || '—'}</td>
                                            <td style={styles.td}>{show.state || '—'}</td>
                                            <td style={styles.td}>{show.country || '—'}</td>
                                            <td style={styles.td}>
                                                {show.venue_id ? <Link to={`/venues/${show.venue_slug || show.venue_id}`} style={styles.tableLink}>{show.venue_name}</Link> : show.venue_name || '—'}
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {show.festival_name && show.festival_slug
                                                    ? <><Link to={`/festivals/${show.festival_slug}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' }}>{show.festival_name}</Link>{show.other_artists ? ` - ${show.other_artists}` : ''}</>
                                                    : show.other_artists || '—'}
                                            </td>
                                            <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {isPerformed ? <span style={styles.performedBadge}>Performed</span>
                                                    : isWorked ? <span style={styles.workedBadge}>Worked</span>
                                                    : <button onClick={() => onToggleAttended(show.event_id)} disabled={isToggling} style={isAttended ? styles.attendedBtnOn : styles.attendedBtnOff}>{isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}</button>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Flyer History Tab (placeholder) ──────────────────────────────────────────
function FlyerHistoryTab() {
    return (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>🎟️</div>
            <div style={{ color: '#888', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Flyer History</div>
            <div style={{ color: '#555', fontSize: '14px' }}>Coming soon — show flyers and poster archive will appear here.</div>
        </div>
    );
}

// ── Shows by Year Tab ─────────────────────────────────────────────────────────
function ShowsByYearTab({ shows }) {
    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded yet.</div>;
    const years = shows.map(s => new Date(s.event_date).getUTCFullYear());
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const counts = {};
    years.forEach(y => { counts[y] = (counts[y] || 0) + 1; });
    const data = [];
    for (let y = minYear; y <= maxYear; y++) data.push({ year: y, count: counts[y] || 0 });
    const maxCount = Math.max(...data.map(d => d.count), 1);
    const barMaxHeight = 200;
    const gridLines = [];
    const step = maxCount <= 5 ? 1 : maxCount <= 10 ? 2 : maxCount <= 20 ? 5 : 10;
    for (let v = 0; v <= maxCount; v += step) gridLines.push(v);
    if (gridLines[gridLines.length - 1] < maxCount) gridLines.push(maxCount);
    const chartWidth = Math.max(data.length * 52, 400);
    const yAxisWidth = 36;
    const chartPadTop = 16;
    const chartPadBottom = 40;
    const totalHeight = barMaxHeight + chartPadTop + chartPadBottom;
    return (
        <div>
            <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                <div style={{ display: 'flex', minWidth: chartWidth + yAxisWidth + 'px' }}>
                    <div style={{ width: yAxisWidth + 'px', flexShrink: 0, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => { const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight; return <div key={v} style={{ position: 'absolute', top: top + 'px', right: '6px', transform: 'translateY(-50%)', fontSize: '11px', color: '#666', whiteSpace: 'nowrap' }}>{v}</div>; })}
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => { const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight; return <div key={v} style={{ position: 'absolute', top: top + 'px', left: 0, right: 0, borderTop: `1px solid ${v === 0 ? '#444' : '#2a2a2a'}`, zIndex: 0 }} />; })}
                        <div style={{ display: 'flex', alignItems: 'flex-end', height: barMaxHeight + 'px', marginTop: chartPadTop + 'px', gap: '4px', paddingLeft: '8px', paddingRight: '8px', position: 'relative', zIndex: 1 }}>
                            {data.map(({ year, count }) => {
                                const barH = count === 0 ? 0 : Math.max((count / maxCount) * barMaxHeight, 4);
                                return (
                                    <div key={year} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }} title={`${year}: ${count} show${count !== 1 ? 's' : ''}`}>
                                        {count > 0 && <div style={{ fontSize: '11px', color: '#bb86fc', fontWeight: 'bold', opacity: 0.85 }}>{count}</div>}
                                        <div style={{ width: '100%', maxWidth: '44px', height: barH + 'px', backgroundColor: count === 0 ? 'transparent' : '#bb86fc', borderRadius: '3px 3px 0 0' }} />
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', paddingLeft: '8px', paddingRight: '8px', gap: '4px', marginTop: '6px' }}>
                            {data.map(({ year, count }) => <div key={year} style={{ flex: 1, textAlign: 'center', fontSize: '11px', color: count === 0 ? '#444' : '#888', whiteSpace: 'nowrap', overflow: 'hidden' }}>{year}</div>)}
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '32px', fontSize: '13px', color: '#888' }}>
                <span>Total Years Active: <strong style={{ color: '#e0e0e0' }}>{data.filter(d => d.count > 0).length}</strong></span>
                <span>Peak Year: <strong style={{ color: '#bb86fc' }}>{data.reduce((best, d) => d.count > best.count ? d : best, data[0]).year} ({data.reduce((best, d) => d.count > best.count ? d : best, data[0]).count} shows)</strong></span>
            </div>
        </div>
    );
}

// ── Festivals Played Tab ──────────────────────────────────────────────────────
function FestivalsPlayedTab({ shows }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const festivalShows = shows ? shows.filter(s => s.festival_id) : [];
    if (festivalShows.length === 0) return <div style={styles.emptyTab}>No festival shows recorded yet.</div>;
    const q = search.toLowerCase();
    const filtered = search ? festivalShows.filter(show => {
        const dateStr = show.event_date ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';
        return [dateStr, show.festival_name, show.city, show.state, show.country, show.venue_name, show.other_artists].some(val => val && val.toLowerCase().includes(q));
    }) : festivalShows;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by date, festival, city, venue, artists..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No festival shows match your search.</div> : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead><tr><th style={styles.th}>Date</th><th style={styles.th}>Festival</th><th style={styles.th}>City</th><th style={styles.th}>State</th><th style={styles.th}>Country</th><th style={styles.th}>Venue</th><th style={styles.th}>Additional Artists</th></tr></thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    const formattedDate = show.event_date ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—';
                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={styles.td}><Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{formattedDate}</Link></td>
                                            <td style={styles.td}>{show.festival_slug ? <Link to={`/festivals/${show.festival_slug}`} style={styles.tableLink}>{show.festival_name}</Link> : show.festival_name || '—'}</td>
                                            <td style={styles.td}>{show.city || '—'}</td>
                                            <td style={styles.td}>{show.state || '—'}</td>
                                            <td style={styles.td}>{show.country || '—'}</td>
                                            <td style={styles.td}>{show.venue_id ? <Link to={`/venues/${show.venue_slug || show.venue_id}`} style={styles.tableLink}>{show.venue_name}</Link> : show.venue_name || '—'}</td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{show.other_artists || '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Venues Played Tab ─────────────────────────────────────────────────────────
function VenuesPlayedTab({ venues }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    if (!venues || venues.length === 0) return <div style={styles.emptyTab}>No venues recorded yet.</div>;
    const q = search.toLowerCase();
    const filtered = search ? venues.filter(v => [v.name, v.city, v.state, v.country, v.capacity != null ? String(v.capacity) : ''].some(val => val && val.toLowerCase().includes(q))) : venues;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by venue, city, state, country, capacity..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No venues match your search.</div> : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead><tr><th style={styles.th}>Venue</th><th style={styles.th}>City</th><th style={styles.th}>State/Region</th><th style={styles.th}>Country</th><th style={styles.th}>Capacity</th><th style={styles.th}>Shows</th></tr></thead>
                            <tbody>
                                {paginated.map((venue, idx) => (
                                    <tr key={venue.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}><Link to={`/venues/${venue.slug || venue.id}`} style={styles.tableLink}>{venue.name}</Link></td>
                                        <td style={styles.td}>{venue.city || '—'}</td>
                                        <td style={styles.td}>{venue.state || '—'}</td>
                                        <td style={styles.td}>{venue.country || '—'}</td>
                                        <td style={styles.td}>{venue.capacity ? venue.capacity.toLocaleString() : '—'}</td>
                                        <td style={styles.td}><span style={styles.showCount}>{venue.times_played}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Artist Relationships Tab ──────────────────────────────────────────────────
function ArtistRelationshipsTab({ playedWith }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    if (!playedWith || playedWith.length === 0) return <div style={styles.emptyTab}>No co-billed artists recorded yet.</div>;
    const q = search.toLowerCase();
    const filtered = search ? playedWith.filter(pw => [pw.name, pw.city, pw.state, pw.country].some(val => val && val.toLowerCase().includes(q))) : playedWith;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by artist, city, state, country..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No artists match your search.</div> : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead><tr><th style={styles.th}>Artist</th><th style={styles.th}>City</th><th style={styles.th}>State/Region</th><th style={styles.th}>Country</th><th style={styles.th}>Shows Together</th></tr></thead>
                            <tbody>
                                {paginated.map((pw, idx) => (
                                    <tr key={pw.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}><Link to={`/artists/${pw.slug || pw.id}`} style={styles.tableLink}>{pw.name}</Link></td>
                                        <td style={styles.td}>{pw.city || '—'}</td>
                                        <td style={styles.td}>{pw.state || '—'}</td>
                                        <td style={styles.td}>{pw.country || '—'}</td>
                                        <td style={styles.td}><span style={styles.showCount}>{pw.times_played_together}x</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Promoter Relationships Tab ────────────────────────────────────────────────
function PromoterRelationshipsTab({ promoterRelationships }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    if (!promoterRelationships || promoterRelationships.length === 0) return <div style={styles.emptyTab}>No promoter relationships recorded yet.</div>;
    const q = search.toLowerCase();
    const filtered = search ? promoterRelationships.filter(p => [p.name, p.city, p.state, p.country].some(val => val && val.toLowerCase().includes(q))) : promoterRelationships;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by promoter, city, state, country..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No promoters match your search.</div> : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead><tr><th style={styles.th}>Promoter</th><th style={styles.th}>City</th><th style={styles.th}>State/Region</th><th style={styles.th}>Country</th><th style={styles.th}>Shows Together</th></tr></thead>
                            <tbody>
                                {paginated.map((p, idx) => (
                                    <tr key={p.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}><Link to={`/promoters/${p.slug || p.id}`} style={styles.tableLink}>{p.name}</Link></td>
                                        <td style={styles.td}>{p.city || '—'}</td>
                                        <td style={styles.td}>{p.state || '—'}</td>
                                        <td style={styles.td}>{p.country || '—'}</td>
                                        <td style={styles.td}><span style={styles.showCount}>{p.times_promoted}x</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Geographic Breakdown Tab (collapsible) ────────────────────────────────────
function GeographicBreakdownTab({ geography }) {
    const [collapsed, setCollapsed] = useState({});

    if (!geography || geography.length === 0) return <div style={styles.emptyTab}>No geographic data available.</div>;

    // Build rollup: country → state → city → venues
    const rollup = {};
    geography.forEach(row => {
        const country = row.country || 'Unknown';
        const state = row.state || '—';
        const city = row.city || '—';
        const count = parseInt(row.show_count) || 0;
        if (!rollup[country]) rollup[country] = { total: 0, states: {} };
        rollup[country].total += count;
        if (!rollup[country].states[state]) rollup[country].states[state] = { total: 0, cities: {} };
        rollup[country].states[state].total += count;
        if (!rollup[country].states[state].cities[city]) rollup[country].states[state].cities[city] = { total: 0, venues: {} };
        rollup[country].states[state].cities[city].total += count;
        const venueKey = `${row.venue_id}__${row.venue_name || '—'}`;
        if (!rollup[country].states[state].cities[city].venues[venueKey]) {
            rollup[country].states[state].cities[city].venues[venueKey] = { id: row.venue_id, name: row.venue_name || '—', slug: row.venue_slug, count: 0 };
        }
        rollup[country].states[state].cities[city].venues[venueKey].count += count;
    });

    const sortedCountries = Object.entries(rollup).sort((a, b) => b[1].total - a[1].total);

    const toggleKey = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
    // All levels collapsed by default; user explicitly opens them
    const isOpen = (key) => {
        if (key in collapsed) return !collapsed[key];
        return false;
    };

    return (
        <div>
            {sortedCountries.map(([country, countryData]) => {
                const countryKey = `c:${country}`;
                const countryOpen = isOpen(countryKey);
                const sortedStates = Object.entries(countryData.states).sort((a, b) => b[1].total - a[1].total);

                return (
                    <div key={country} style={styles.geoGroup}>
                        {/* Country header */}
                        <div style={styles.geoCountryRow} onClick={() => toggleKey(countryKey)}>
                            <span style={styles.geoChevron}>{countryOpen ? '▾' : '▸'}</span>
                            <span style={styles.geoCountryName}>{country}</span>
                            <span style={styles.geoCount}>{countryData.total} show{countryData.total !== 1 ? 's' : ''}</span>
                        </div>

                        {countryOpen && sortedStates.map(([state, stateData]) => {
                            const stateKey = `s:${country}:${state}`;
                            const stateOpen = isOpen(stateKey);
                            const sortedCities = Object.entries(stateData.cities).sort((a, b) => b[1].total - a[1].total);

                            return (
                                <div key={state} style={styles.geoStateBlock}>
                                    {/* State header */}
                                    <div style={styles.geoStateRow} onClick={() => toggleKey(stateKey)}>
                                        <span style={styles.geoChevron}>{stateOpen ? '▾' : '▸'}</span>
                                        <span style={styles.geoStateName}>{state}</span>
                                        <span style={styles.geoCount}>{stateData.total} show{stateData.total !== 1 ? 's' : ''}</span>
                                    </div>

                                    {stateOpen && sortedCities.map(([city, cityData]) => {
                                        const cityKey = `ci:${country}:${state}:${city}`;
                                        const cityOpen = isOpen(cityKey);
                                        const sortedVenues = Object.values(cityData.venues).sort((a, b) => b.count - a.count);

                                        return (
                                            <div key={city} style={styles.geoCityBlock}>
                                                {/* City header */}
                                                <div style={styles.geoCityRow} onClick={() => toggleKey(cityKey)}>
                                                    <span style={styles.geoChevron}>{cityOpen ? '▾' : '▸'}</span>
                                                    <span style={styles.geoCityName}>{city}</span>
                                                    <span style={styles.geoCount}>{cityData.total} show{cityData.total !== 1 ? 's' : ''}</span>
                                                </div>

                                                {cityOpen && (
                                                    <div style={styles.geoVenueList}>
                                                        {sortedVenues.map(venue => (
                                                            <div key={venue.id || venue.name} style={styles.geoVenueRow}>
                                                                <span style={styles.geoVenueName}>
                                                                    {venue.id
                                                                        ? <Link to={`/venues/${venue.slug || venue.id}`} style={styles.tableLink}>{venue.name}</Link>
                                                                        : <span style={{ color: '#aaa' }}>{venue.name}</span>}
                                                                </span>
                                                                <span style={styles.showCount}>{venue.count}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

// ── Geographic Map Tab ────────────────────────────────────────────────────────
function GeographicMapTab({ showMap }) {
    const mapRef = React.useRef(null);
    const mapInstanceRef = React.useRef(null);
    const [leafletReady, setLeafletReady] = React.useState(!!window.L);
    React.useEffect(() => {
        if (window.L) { setLeafletReady(true); return; }
        if (!document.getElementById('leaflet-css')) { const link = document.createElement('link'); link.id = 'leaflet-css'; link.rel = 'stylesheet'; link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(link); }
        const script = document.createElement('script'); script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'; script.onload = () => setLeafletReady(true); document.head.appendChild(script);
    }, []);
    React.useEffect(() => {
        if (!leafletReady || !mapRef.current || !showMap || showMap.length === 0) return;
        if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
        const L = window.L;
        const map = L.map(mapRef.current, { zoomControl: true });
        mapInstanceRef.current = map;
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap contributors', maxZoom: 18 }).addTo(map);
        const markerIcon = L.divIcon({ className: '', html: `<div style="width:12px;height:12px;background:#bb86fc;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.5);"></div>`, iconSize: [12, 12], iconAnchor: [6, 6], popupAnchor: [0, -8] });
        const bounds = [];
        showMap.forEach(venue => {
            const lat = parseFloat(venue.lat); const lng = parseFloat(venue.lng);
            if (isNaN(lat) || isNaN(lng)) return;
            bounds.push([lat, lng]);
            const dates = venue.show_dates ? venue.show_dates.split(',').map(d => new Date(d.trim()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })) : [];
            const locationStr = [venue.city, venue.state, venue.country].filter(Boolean).join(', ');
            const showCount = parseInt(venue.show_count);
            const popupHtml = `<div style="font-family:sans-serif;min-width:180px;max-width:260px;"><div style="font-weight:bold;font-size:14px;margin-bottom:4px;"><a href="/venues/${venue.venue_slug || venue.venue_id}" style="color:#bb86fc;text-decoration:none;">${venue.venue_name} ↗</a></div>${locationStr ? `<div style="font-size:12px;color:#888;margin-bottom:6px;">${locationStr}</div>` : ''}<div style="font-size:12px;color:#aaa;margin-bottom:${dates.length ? '6px' : '0'};"><strong style="color:#e0e0e0;">${showCount}</strong> show${showCount !== 1 ? 's' : ''}</div>${dates.length > 0 ? `<div style="font-size:11px;color:#999;line-height:1.6;">${dates.slice(0, 10).join('<br/>')}${dates.length > 10 ? `<br/><em>+${dates.length - 10} more</em>` : ''}</div>` : ''}</div>`;
            L.marker([lat, lng], { icon: markerIcon }).bindPopup(popupHtml, { maxWidth: 280 }).addTo(map);
        });
        if (bounds.length === 1) map.setView(bounds[0], 12);
        else if (bounds.length > 1) map.fitBounds(bounds, { padding: [40, 40] });
        return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
    }, [leafletReady, showMap]);
    if (!showMap || showMap.length === 0) return <div style={styles.emptyTab}>No geocoded venues available yet.</div>;
    return (
        <div>
            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#888' }}>{showMap.length} venue{showMap.length !== 1 ? 's' : ''} with location data · Click a pin for details</div>
            {!leafletReady ? <div style={styles.emptyTab}>Loading map...</div> : <div ref={mapRef} style={{ height: '480px', borderRadius: '8px', border: '1px solid #333', overflow: 'hidden' }} />}
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
function ArtistDetail({ slugOverride } = {}) {
    const params = useParams();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeInfoTab, setActiveInfoTab] = useState(0);

    // Section 3 parent tab + sub-tab state
    const [activeParentTab, setActiveParentTab] = useState(0);
    const [activeShowArchiveSubTab, setActiveShowArchiveSubTab] = useState(0);
    const [activeRelationshipsSubTab, setActiveRelationshipsSubTab] = useState(0);
    const [activeLocationsSubTab, setActiveLocationsSubTab] = useState(0);

    const [defaultImageId, setDefaultImageId] = useState(null);
    const [defaultImageUrl, setDefaultImageUrl] = useState(null);

    const [attendedMap, setAttendedMap] = useState({});
    const [performedMap, setPerformedMap] = useState({});
    const [workedMap, setWorkedMap] = useState({});
    const [togglingMap, setTogglingMap] = useState({});

    const loadAttendedStatus = useCallback(async (shows) => {
        if (!shows || shows.length === 0) return;
        try {
            const eventIds = shows.map(s => s.event_id);
            const statusMap = await fetchBatchEventStatus(eventIds);
            const aMap = {}; const pMap = {}; const wMap = {};
            Object.entries(statusMap).forEach(([id, status]) => { aMap[id] = status.attended; pMap[id] = status.performed || false; wMap[id] = status.worked || false; });
            setAttendedMap(aMap); setPerformedMap(pMap); setWorkedMap(wMap);
        } catch { /* not logged in */ }
    }, []);

    const handleToggleAttended = useCallback(async (eventId) => {
        setTogglingMap(prev => ({ ...prev, [eventId]: true }));
        try {
            const result = await toggleAttended(eventId);
            setAttendedMap(prev => ({ ...prev, [eventId]: result.attended }));
            setPerformedMap(prev => ({ ...prev, [eventId]: result.performed || false }));
            setWorkedMap(prev => ({ ...prev, [eventId]: result.worked || false }));
        } catch (e) { console.error('toggleAttended error:', e); }
        finally { setTogglingMap(prev => ({ ...prev, [eventId]: false })); }
    }, []);

    useEffect(() => {
        async function load() {
            setDefaultImageId(null);
            setDefaultImageUrl(null);
            try {
                const res = await axios.get(`${config.API_URL}/artists/${slug}`);
                setData(res.data);
                setLoading(false);
                if (res.data?.artist) document.title = `SHARC - ${res.data.artist.name}`;
                if (res.data?.artist?.default_image_id) {
                    setDefaultImageId(res.data.artist.default_image_id);
                    if (res.data.artist.default_image_url) {
                        setDefaultImageUrl(res.data.artist.default_image_url);
                    }
                }
                if (res.data?.show_history) loadAttendedStatus(res.data.show_history);
            } catch {
                setError('Failed to load artist');
                setLoading(false);
            }
        }
        load();
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, [slug, loadAttendedStatus]);

    const handleDefaultChanged = useCallback((imageId, imageUrl) => {
        setDefaultImageId(imageId);
        setDefaultImageUrl(imageUrl);
    }, []);

    if (loading) return <div style={styles.message}>Loading artist...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const { artist, stats, played_with, promoter_relationships, top_venues, geography, show_history, show_map, formerly_known_as, hometowns, links } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
    };

    const heroImage = defaultImageUrl || artist.image_url;

    return (
        <div>
            {/* ── Section 1: Artist Name + Edit button ── */}
            <div style={styles.section1}>
                <h1 style={styles.artistNameHeading}>{artist.name}</h1>
                <Link to={`/artists/${artist.slug || artist.id}/edit`} style={styles.editButton}>Edit Artist</Link>
            </div>

            {/* ── Section 2: Image/meta left + info tabs right ── */}
            <div style={styles.headerGrid}>
                {/* Left: image + location + type/genre */}
                <div style={styles.headerLeft}>
                    <div style={styles.headerImageRow}>
                        {heroImage ? (
                            <img
                                src={heroImage}
                                alt={artist.name}
                                style={styles.artistImage}
                                onError={e => { e.target.outerHTML = `<div style="width:160px;height:160px;border-radius:8px;border:1px solid #333;background:#1e1e1e;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;flex-shrink:0"><div style="font-size:32px;opacity:0.3">🎵</div><div style="font-size:11px;color:#555;text-transform:uppercase;letter-spacing:0.5px">No Image</div></div>`; }}
                            />
                        ) : (
                            <div style={styles.noImageBox}>
                                <div style={styles.noImageIcon}>🎵</div>
                                <div style={styles.noImageLabel}>No Image</div>
                            </div>
                        )}
                        <div style={styles.headerMeta}>
                            {[artist.city, artist.state, artist.country].filter(Boolean).length > 0 && (
                                <div style={styles.location}>{[artist.city, artist.state, artist.country].filter(Boolean).join(', ')}</div>
                            )}
                            {artist.artist_type && (
                                <span style={styles.tag}>{artist.artist_type}</span>
                            )}
                            {artist.artist_style && (
                                <span style={{ ...styles.tag, marginTop: '4px' }}>{artist.artist_style}</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: info tabs */}
                <div style={styles.headerRight}>
                    <div style={styles.infoTabBar}>
                        {INFO_TABS.map((tab, i) => (
                            <button
                                key={tab}
                                onClick={() => setActiveInfoTab(i)}
                                style={{ ...styles.infoTabBtn, ...(activeInfoTab === i ? styles.infoTabBtnActive : {}) }}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    <div style={styles.infoTabContent}>
                        {/* Show Stats */}
                        {activeInfoTab === 0 && (() => {
                            let termStr = '—';
                            if (stats.first_show_date && stats.last_show_date) {
                                let d1 = new Date(stats.first_show_date); let d2 = new Date(stats.last_show_date);
                                let years = d2.getUTCFullYear() - d1.getUTCFullYear(); let months = d2.getUTCMonth() - d1.getUTCMonth(); let days = d2.getUTCDate() - d1.getUTCDate();
                                if (days < 0) { months -= 1; days += new Date(d2.getUTCFullYear(), d2.getUTCMonth(), 0).getDate(); }
                                if (months < 0) { years -= 1; months += 12; }
                                const parts = [];
                                if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
                                if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
                                if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
                                termStr = parts.join(' ') || '—';
                            }
                            return (
                                <div style={styles.showStatsLayout}>
                                    <div style={styles.totalShowsBox}>
                                        <div style={styles.statValue}>{stats.total_shows || 0}</div>
                                        <div style={styles.statLabel}>Total Shows</div>
                                    </div>
                                    <table style={styles.statsTable}>
                                        <tbody>
                                            <tr>
                                                <td style={styles.statsTableLabel}>First Show</td>
                                                <td style={styles.statsTableValue}>{formatDate(stats.first_show_date)}</td>
                                            </tr>
                                            <tr>
                                                <td style={styles.statsTableLabel}>Most Recent Show</td>
                                                <td style={styles.statsTableValue}>{formatDate(stats.last_show_date)}</td>
                                            </tr>
                                            <tr>
                                                <td style={styles.statsTableLabel}>Term of Shows</td>
                                                <td style={styles.statsTableValue}>{termStr}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })()}

                        {/* Bio */}
                        {activeInfoTab === 1 && (
                            <div>
                                {artist.description
                                    ? <div style={styles.bioText}>{artist.description}</div>
                                    : <div style={styles.emptyInfo}>No bio available.</div>}
                            </div>
                        )}

                        {/* Links */}
                        {activeInfoTab === 2 && (
                            <div>
                                {links && links.length > 0
                                    ? <div style={styles.linksGrid}>
                                        {links.map(link => (
                                            <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" style={styles.linkChip}>
                                                {link.description} ↗
                                            </a>
                                        ))}
                                    </div>
                                    : <div style={styles.emptyInfo}>No links added yet.</div>}
                            </div>
                        )}

                        {/* Photos */}
                        {activeInfoTab === 3 && (
                            <PhotosTab
                                artistId={artist.id}
                                artistName={artist.name}
                                defaultImageId={defaultImageId}
                                onDefaultChanged={handleDefaultChanged}
                            />
                        )}

                        {/* Additional Info */}
                        {activeInfoTab === 4 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {hometowns && hometowns.length > 0 && (
                                    <div>
                                        <div style={styles.infoLabel}>Hometown</div>
                                        <div style={styles.infoValue}>
                                            {hometowns.map(ht => [ht.city, ht.state, ht.country].filter(Boolean).join(', ')).join(' · ')}
                                        </div>
                                    </div>
                                )}
                                {formerly_known_as && formerly_known_as.length > 0 && (
                                    <div>
                                        <div style={styles.infoLabel}>Formerly Known As</div>
                                        <div style={styles.infoValue}>{formerly_known_as.map(f => f.name).join(', ')}</div>
                                    </div>
                                )}
                                {(!hometowns || hometowns.length === 0) && (!formerly_known_as || formerly_known_as.length === 0) && (
                                    <div style={styles.emptyInfo}>No additional information available.</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Section 3: Multi-level tabs ── */}
            <div style={styles.tabsWrapper}>
                {/* Parent tab bar */}
                <div style={styles.tabBar}>
                    {PARENT_TABS.map((tab, i) => (
                        <button key={tab} onClick={() => setActiveParentTab(i)} style={{ ...styles.tabBtn, ...(activeParentTab === i ? styles.tabBtnActive : {}) }}>{tab}</button>
                    ))}
                </div>

                <div style={styles.tabContent}>
                    {/* ── Show Archive ── */}
                    {activeParentTab === 0 && (
                        <div>
                            <SubTabBar tabs={SHOW_ARCHIVE_SUBTABS} active={activeShowArchiveSubTab} onChange={setActiveShowArchiveSubTab} />
                            <div style={styles.subTabContent}>
                                {activeShowArchiveSubTab === 0 && (
                                    <ShowHistoryTab
                                        shows={show_history}
                                        attendedMap={attendedMap}
                                        performedMap={performedMap}
                                        workedMap={workedMap}
                                        onToggleAttended={handleToggleAttended}
                                        togglingMap={togglingMap}
                                    />
                                )}
                                {activeShowArchiveSubTab === 1 && <FlyerHistoryTab />}
                            </div>
                        </div>
                    )}

                    {/* ── Shows by Year ── */}
                    {activeParentTab === 1 && <ShowsByYearTab shows={show_history} />}

                    {/* ── Members ── */}
                    {activeParentTab === 2 && <ArtistMembersTab artistId={artist.id} />}

                    {/* ── Relationships ── */}
                    {activeParentTab === 3 && (
                        <div>
                            <SubTabBar tabs={RELATIONSHIPS_SUBTABS} active={activeRelationshipsSubTab} onChange={setActiveRelationshipsSubTab} />
                            <div style={styles.subTabContent}>
                                {activeRelationshipsSubTab === 0 && <ArtistRelationshipsTab playedWith={played_with} />}
                                {activeRelationshipsSubTab === 1 && <FestivalsPlayedTab shows={show_history} />}
                                {activeRelationshipsSubTab === 2 && <PromoterRelationshipsTab promoterRelationships={promoter_relationships} />}
                                {activeRelationshipsSubTab === 3 && <VenuesPlayedTab venues={top_venues} />}
                            </div>
                        </div>
                    )}

                    {/* ── Locations ── */}
                    {activeParentTab === 4 && (
                        <div>
                            <SubTabBar tabs={LOCATIONS_SUBTABS} active={activeLocationsSubTab} onChange={setActiveLocationsSubTab} />
                            <div style={styles.subTabContent}>
                                {activeLocationsSubTab === 0 && <GeographicBreakdownTab geography={geography} />}
                                {activeLocationsSubTab === 1 && <GeographicMapTab showMap={show_map} />}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    // ── Section 1 ──
    section1: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    artistNameHeading: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0, lineHeight: 1.2 },
    editButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '6px 16px', fontSize: '14px', textDecoration: 'none', fontWeight: 'bold', flexShrink: 0 },

    // ── Section 2 header grid ──
    headerGrid: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px', marginBottom: '32px', alignItems: 'start' },
    headerLeft: { display: 'flex', flexDirection: 'column', gap: '12px' },
    headerImageRow: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    artistImage: { width: '160px', height: '160px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', flexShrink: 0 },
    noImageBox: { width: '160px', height: '160px', borderRadius: '8px', border: '1px solid #333', background: '#1e1e1e', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', flexShrink: 0 },
    noImageIcon: { fontSize: '32px', opacity: 0.3 },
    noImageLabel: { fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' },
    headerMeta: { display: 'flex', flexDirection: 'column', gap: '4px', paddingTop: '4px' },
    location: { fontSize: '15px', color: '#aaa', marginBottom: '6px' },
    tag: { display: 'inline-block', backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#bb86fc' },

    // ── Info tabs (right side of section 2) ──
    headerRight: {},
    infoTabBar: { display: 'flex', borderBottom: '2px solid #333', gap: '2px', overflowX: 'auto' },
    infoTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '10px 16px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap' },
    infoTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    infoTabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '16px', minHeight: '160px', maxHeight: '280px', overflowY: 'auto' },
    bioText: { fontSize: '14px', color: '#ccc', lineHeight: '1.7', maxHeight: '96px', overflowY: 'auto' },

    // ── Show Stats tab ──
    showStatsLayout: { display: 'flex', gap: '24px', alignItems: 'center', height: '100%' },
    totalShowsBox: { flexShrink: 0, backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px 28px', textAlign: 'center', minWidth: '110px' },
    statsTable: { flex: 1, borderCollapse: 'collapse', fontSize: '14px' },
    statsTableLabel: { padding: '10px 16px 10px 0', color: '#aaa', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', width: '1%' },
    statsTableValue: { padding: '10px 0', color: '#e0e0e0', fontWeight: 'bold', fontSize: '15px', borderBottom: '1px solid #2a2a2a' },
    linksGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    linkChip: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 14px', fontSize: '13px', color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    emptyInfo: { color: '#555', fontSize: '14px', padding: '20px 0', textAlign: 'center' },
    infoLabel: { fontSize: '11px', color: '#777', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
    infoValue: { fontSize: '14px', color: '#ccc' },

    // ── Photos tab ──
    photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' },
    photoCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' },
    photoCardDefault: { borderColor: '#bb86fc' },
    photoThumb: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', cursor: 'zoom-in', background: '#111' },
    photoThumbImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    defaultBadge: { position: 'absolute', top: '6px', left: '6px', background: '#bb86fc', color: '#121212', fontSize: '10px', fontWeight: 'bold', borderRadius: '4px', padding: '2px 6px' },
    privateBadge: { position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px', fontSize: '12px' },
    photoCardBody: { padding: '8px' },
    photoCaption: { fontSize: '11px', color: '#aaa', marginBottom: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    setDefaultBtn: { width: '100%', background: 'transparent', border: '1px solid #bb86fc', borderRadius: '4px', color: '#bb86fc', fontSize: '11px', padding: '4px 6px', cursor: 'pointer' },
    isDefaultLabel: { fontSize: '11px', color: '#bb86fc', textAlign: 'center', padding: '4px 0' },
    addPhotoBtn: { background: '#bb86fc', color: '#121212', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
    lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 },
    lightboxCaption: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: '#e0e0e0', padding: '8px 12px', fontSize: '13px', textAlign: 'center', borderRadius: '0 0 8px 8px' },
    lightboxClose: { position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', borderRadius: '4px', padding: '2px 8px' },

    // ── Stat value/label ──
    statValue: { fontSize: '36px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },

    // ── Section 3: Parent tabs ──
    tabsWrapper: { marginTop: '8px' },
    tabBar: { display: 'flex', borderBottom: '2px solid #333', marginBottom: '0', overflowX: 'auto', gap: '2px' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '12px 20px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px' },
    tabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '200px' },

    // ── Sub-tabs (inside parent tab content) ──
    subTabBar: { display: 'flex', borderBottom: '1px solid #2e2e2e', gap: '2px', marginBottom: '0', overflowX: 'auto' },
    subTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-1px', padding: '8px 16px', fontSize: '13px', fontWeight: '500', color: '#666', cursor: 'pointer', whiteSpace: 'nowrap' },
    subTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    subTabContent: { paddingTop: '20px' },

    // ── Tables ──
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '11px 16px', color: '#e0e0e0', borderBottom: '1px solid #222' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    showCount: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    attendedBtnOff: { backgroundColor: 'transparent', color: '#666', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    performedBadge: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    workedBadge: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    pagination: { display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '20px', flexWrap: 'wrap' },
    pageBtn: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 12px', fontSize: '13px', color: '#aaa', cursor: 'pointer' },
    pageBtnActive: { backgroundColor: '#bb86fc', borderColor: '#bb86fc', color: '#121212', fontWeight: 'bold' },
    pageBtnDisabled: { opacity: 0.35, cursor: 'default' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '40px 0', fontSize: '15px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },

    // ── Geographic Breakdown (collapsible) ──
    geoGroup: { marginBottom: '4px' },
    geoCountryRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: '#222', borderRadius: '6px', cursor: 'pointer', userSelect: 'none', marginBottom: '2px' },
    geoCountryName: { flex: 1, fontSize: '14px', fontWeight: 'bold', color: '#e0e0e0' },
    geoStateBlock: { marginLeft: '16px', marginBottom: '2px' },
    geoStateRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#1e1e1e', borderRadius: '5px', cursor: 'pointer', userSelect: 'none', marginBottom: '2px' },
    geoStateName: { flex: 1, fontSize: '13px', fontWeight: '600', color: '#ccc' },
    geoCityBlock: { marginLeft: '16px', marginBottom: '2px' },
    geoCityRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', backgroundColor: '#1a1a1a', borderRadius: '4px', cursor: 'pointer', userSelect: 'none', marginBottom: '1px' },
    geoCityName: { flex: 1, fontSize: '13px', color: '#bbb' },
    geoVenueList: { marginLeft: '16px', paddingBottom: '4px' },
    geoVenueRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid #222' },
    geoVenueName: { fontSize: '13px', color: '#aaa', flex: 1 },
    geoChevron: { fontSize: '12px', color: '#555', width: '12px', flexShrink: 0 },
    geoCount: { fontSize: '12px', color: '#555', whiteSpace: 'nowrap' },
};

export default ArtistDetail;
